const OAUTH_HISTORY_CHECKPOINT_KEY = 'minimanyOAuthHistoryCheckpoint'
const OAUTH_CALLBACK_PARAMETERS = [
  'code',
  'error',
  'error_code',
  'error_description',
  'error_uri',
  'state',
] as const

interface OAuthHistoryCheckpoint {
  historyLength: number
  returnUrl: string
}

export interface OAuthHistoryEnvironment {
  history: Pick<History, 'go' | 'length' | 'replaceState' | 'state'>
  location: Pick<Location, 'hash' | 'pathname' | 'search'>
  sessionStorage: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>
}

export type OAuthHistoryCompletion = 'not-callback' | 'replaced' | 'traversed'

function currentBrowser(): OAuthHistoryEnvironment {
  return window
}

export function hasOAuthCallbackParameters(search = window.location.search): boolean {
  const parameters = new URLSearchParams(search)
  return OAUTH_CALLBACK_PARAMETERS.some((parameter) => parameters.has(parameter))
}

export function beginOAuthHistoryNavigation(
  browser: OAuthHistoryEnvironment = currentBrowser(),
): void {
  const checkpoint: OAuthHistoryCheckpoint = {
    historyLength: browser.history.length,
    returnUrl: `${browser.location.pathname}${browser.location.search}${browser.location.hash}`,
  }
  try {
    browser.sessionStorage.setItem(OAUTH_HISTORY_CHECKPOINT_KEY, JSON.stringify(checkpoint))
  } catch {
    // OAuth remains usable when storage is unavailable; callback cleanup falls back to replaceState.
  }
}

export function cancelOAuthHistoryNavigation(
  browser: OAuthHistoryEnvironment = currentBrowser(),
): void {
  try {
    browser.sessionStorage.removeItem(OAUTH_HISTORY_CHECKPOINT_KEY)
  } catch {
    // Nothing else is required when session storage is unavailable.
  }
}

function readCheckpoint(browser: OAuthHistoryEnvironment): OAuthHistoryCheckpoint | null {
  try {
    const stored = browser.sessionStorage.getItem(OAUTH_HISTORY_CHECKPOINT_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as Partial<OAuthHistoryCheckpoint>
    if (
      !Number.isInteger(parsed.historyLength) ||
      (parsed.historyLength ?? 0) < 1 ||
      typeof parsed.returnUrl !== 'string' ||
      !parsed.returnUrl.startsWith('/')
    ) {
      return null
    }
    return parsed as OAuthHistoryCheckpoint
  } catch {
    return null
  }
}

function callbackFreeUrl(browser: OAuthHistoryEnvironment): string {
  const parameters = new URLSearchParams(browser.location.search)
  for (const parameter of OAUTH_CALLBACK_PARAMETERS) parameters.delete(parameter)
  const search = parameters.toString()
  const hasAuthHash = /(?:^#|[&#])(access_token|refresh_token|error)=/i.test(browser.location.hash)
  return `${browser.location.pathname}${search ? `?${search}` : ''}${hasAuthHash ? '' : browser.location.hash}`
}

export function completeOAuthHistoryNavigation(
  browser: OAuthHistoryEnvironment = currentBrowser(),
): OAuthHistoryCompletion {
  const checkpoint = readCheckpoint(browser)
  if (!hasOAuthCallbackParameters(browser.location.search) && !checkpoint) return 'not-callback'
  cancelOAuthHistoryNavigation(browser)
  const historyDelta = checkpoint ? browser.history.length - checkpoint.historyLength : 0
  if (historyDelta > 0) {
    browser.history.go(-historyDelta)
    return 'traversed'
  }

  browser.history.replaceState(
    browser.history.state,
    '',
    checkpoint?.returnUrl ?? callbackFreeUrl(browser),
  )
  return 'replaced'
}
